import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

export const CustomCursor = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      setIsVisible(true);
    };

    const handleMouseLeave = () => setIsVisible(false);
    const handleMouseEnter = () => setIsVisible(true);

    // Add hover detection for interactive elements
    const handleElementHover = () => {
      const hoverElements = document.querySelectorAll('a, button, [data-cursor-hover]');
      hoverElements.forEach((el) => {
        el.addEventListener('mouseenter', () => setIsHovering(true));
        el.addEventListener('mouseleave', () => setIsHovering(false));
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    document.body.addEventListener('mouseleave', handleMouseLeave);
    document.body.addEventListener('mouseenter', handleMouseEnter);
    
    // Initial hover setup and re-run on DOM changes
    handleElementHover();
    const observer = new MutationObserver(handleElementHover);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.body.removeEventListener('mouseleave', handleMouseLeave);
      document.body.removeEventListener('mouseenter', handleMouseEnter);
      observer.disconnect();
    };
  }, []);

  // Don't render on touch devices
  if (typeof window !== 'undefined' && 'ontouchstart' in window) {
    return null;
  }

  return (
    <>
      {/* Main cursor ring */}
      <motion.div
        className="pointer-events-none fixed top-0 left-0 z-[9999] hidden md:block"
        animate={{
          x: mousePosition.x - 20,
          y: mousePosition.y - 20,
          scale: isHovering ? 1.5 : 1,
          opacity: isVisible ? 1 : 0,
        }}
        transition={{
          type: 'spring',
          stiffness: 500,
          damping: 28,
          mass: 0.5,
        }}
      >
        <div
          className={`w-10 h-10 rounded-full border-2 transition-colors duration-200 ${
            isHovering
              ? 'border-secondary bg-secondary/10'
              : 'border-primary bg-transparent'
          }`}
        />
      </motion.div>

      {/* Center dot */}
      <motion.div
        className="pointer-events-none fixed top-0 left-0 z-[9999] hidden md:block"
        animate={{
          x: mousePosition.x - 4,
          y: mousePosition.y - 4,
          opacity: isVisible ? 1 : 0,
        }}
        transition={{
          type: 'spring',
          stiffness: 1000,
          damping: 35,
        }}
      >
        <div
          className={`w-2 h-2 rounded-full transition-colors duration-200 ${
            isHovering ? 'bg-secondary' : 'bg-primary'
          }`}
        />
      </motion.div>

      {/* Trailing particles */}
      {[...Array(3)].map((_, i) => (
        <motion.div
          key={i}
          className="pointer-events-none fixed top-0 left-0 z-[9998] hidden md:block"
          animate={{
            x: mousePosition.x - 3,
            y: mousePosition.y - 3,
            opacity: isVisible ? 0.3 - i * 0.1 : 0,
            scale: 1 - i * 0.2,
          }}
          transition={{
            type: 'spring',
            stiffness: 400 - i * 100,
            damping: 30 + i * 5,
          }}
        >
          <div className="w-1.5 h-1.5 rounded-full bg-primary/40" />
        </motion.div>
      ))}
    </>
  );
};

export default CustomCursor;
